const BLUR_OVERLAY_ID = 'invigilate-blur-overlay';
const ROOT_BLUR_CLASS = 'invigilate-root-blurred';

let violationBlurLock = false;
/** After "Tap to view", skip re-blur until the page is concealed again. */
let userDismissedBlur = false;
/** Ignore viewport screenshot heuristics during fullscreen transitions. */
let suppressViewportBlurUntil = 0;

function clearBlurDom(): void {
  document.getElementById('root')?.classList.remove(ROOT_BLUR_CLASS);
  document.getElementById(BLUR_OVERLAY_ID)?.remove();
}

function dismissBlurOverlay(): void {
  userDismissedBlur = true;
  clearBlurDom();
}

function attachOverlayDismiss(overlay: HTMLElement): void {
  overlay.addEventListener('pointerup', (event) => {
    // Avoid treating scroll/drag as dismiss; primary button / touch only.
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    dismissBlurOverlay();
  });
}

export function setViolationBlurLock(locked: boolean): void {
  violationBlurLock = locked;
  if (locked) {
    userDismissedBlur = false;
    applyBlur();
  }
}

export function isViolationBlurLocked(): boolean {
  return violationBlurLock;
}

/** Call when the page leaves / loses focus so the next return keeps blur until tap. */
export function notifyPageConcealed(): void {
  userDismissedBlur = false;
  applyBlur();
}

/** Fullscreen exit/enter changes the viewport a lot — don't treat that as a screenshot. */
export function suppressViewportBlur(ms = 800): void {
  suppressViewportBlurUntil = Date.now() + ms;
}

export function applyBlur(): void {
  // Always honor dismiss, including during a violation warning.
  // Lock only forces blur when first engaged (via setViolationBlurLock / notifyPageConcealed).
  if (userDismissedBlur) {
    return;
  }

  const root = document.getElementById('root');
  root?.classList.add(ROOT_BLUR_CLASS);

  let overlay = document.getElementById(BLUR_OVERLAY_ID) as HTMLElement | null;

  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = BLUR_OVERLAY_ID;
    overlay.className = 'invigilate-blur-overlay';
    overlay.setAttribute('role', 'button');
    overlay.setAttribute('aria-label', 'Tap to view');

    const message = document.createElement('p');
    message.className = 'invigilate-blur-message';
    message.textContent = 'Tap to view';
    overlay.appendChild(message);

    attachOverlayDismiss(overlay);
    document.documentElement.appendChild(overlay);
  }
}

export function clearBlur(): void {
  if (violationBlurLock) {
    return;
  }
  clearBlurDom();
}

/** Clear blur even while a violation modal is open (e.g. user tapped Ok / Tap to view). */
export function forceClearBlur(): void {
  userDismissedBlur = true;
  clearBlurDom();
}

export function syncBlurWithPageVisibility(): void {
  if (document.visibilityState === 'hidden' || !document.hasFocus()) {
    notifyPageConcealed();
  }
}

/** Native WebView apps can call this when they detect a screenshot. */
export function registerNativeScreenshotBridge(): void {
  (
    window as Window & { invigilateOnScreenshot?: () => void }
  ).invigilateOnScreenshot = () => {
    userDismissedBlur = false;
    applyBlur();
  };
}

export function initMobileScreenshotGuards(isMobile: boolean): () => void {
  if (!isMobile) {
    return () => {};
  }

  registerNativeScreenshotBridge();

  const viewport = window.visualViewport;
  let lastViewportHeight = viewport?.height ?? window.innerHeight;

  const handleViewportChange = () => {
    const nextHeight = viewport?.height ?? window.innerHeight;
    const delta = lastViewportHeight - nextHeight;
    lastViewportHeight = nextHeight;

    if (Date.now() < suppressViewportBlurUntil) {
      return;
    }

    // Screenshot UI usually shrinks the visual viewport; fullscreen exit often grows it.
    if (delta > 40) {
      userDismissedBlur = false;
      applyBlur();
    }
  };

  viewport?.addEventListener('resize', handleViewportChange);
  viewport?.addEventListener('scroll', handleViewportChange);
  window.addEventListener('resize', handleViewportChange);

  return () => {
    viewport?.removeEventListener('resize', handleViewportChange);
    viewport?.removeEventListener('scroll', handleViewportChange);
    window.removeEventListener('resize', handleViewportChange);
    delete (window as Window & { invigilateOnScreenshot?: () => void })
      .invigilateOnScreenshot;
  };
}
